import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query'; // Added useQueryClient
import { KTIcon, KTSVG } from '../../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import {
  delete_requisition,
  // get_all_requisitions, // Will be replaced by get_filtered_requisitions
  get_filtered_requisitions,
  get_requisitions_status // For status updates
} from '../api/requisitionsApi';
import { formatDateFlatpickr, formatDateWithTime, formatDateWithoutTime } from '../../../../helper-functions/DateFunction';
import { permissionCheck } from '../../../../helper-functions/PermissionCheck';
import Flatpickr from 'react-flatpickr';
import { get_all_requisition_type } from '../../requisitionsType/api/requisitionsTypeApi';

// --- Type Definitions ---
export type Requisition = {
  id: number;
  title: string | undefined;
  description: string | undefined;
  due_date: string;
  status: string; // e.g., "Pending", "Accepted", "Rejected"
  requisition_type_id: number | undefined;
  requisition_type_title: string | undefined;
  updated_by_name: string;
  updated_at: string;
  has_quotation: number; // 0 or 1
  // requisition_products and requisition_assignees are complex,
  // ensure they are correctly typed if used directly in the list.
  // For simplicity in the list, they might be omitted or handled differently.
};

type RequisitionType = {
  id: number;
  title: string | undefined;
  // updated_by: string | undefined; // From your original type, may not be needed for dropdown
  // updated_by_name: string | undefined;
  // updated_at: string;
};

type ApiPaginationInfo = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
};

type ApiRequisitionsDataPayload = {
    requisitions: Requisition[]; // Changed from 'users' or 'products'
    pagination: ApiPaginationInfo;
    links: { /* ... link structure ... */ };
};

type ApiRequisitionsFullResponse = {
    data: { // From Axios
        success: boolean;
        message: string;
        data: ApiRequisitionsDataPayload; // From your API structure
    };
};
// --- End Type Definitions ---


var requisition_add: boolean, requisition_edit: boolean, requisition_view: boolean, requisition_logs: boolean, requisition_delete: boolean;

function RequisitionsList() {
  const [requisitionList, setRequisitionList] = useState<Requisition[]>([]); // For current page

  // Filter States
  const [searchInput, setSearchInput] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [requisitionTypeId, setRequisitionTypeId] = useState<string>(''); // Store as string for select, convert to number for API

  // State for API filter payload
   const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
    requisitionTypeId: '',
    fromDate: '',
    toDate: '',
    // searchTerm: '', // If backend supports search
    // statusFilter: '', // If backend supports status filter
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
  const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);

  // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // For server-side sorting

  const [selectedRequisition, setSelectedRequisition] = useState<Requisition | null>(null);
  
  const fromDateRef = useRef<Flatpickr>(null);
  const toDateRef = useRef<Flatpickr>(null);
  const [requisitionTypeList, setRequisitionTypeList] = useState<RequisitionType[]>([]);
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  requisition_add = permissionCheck('requisition-create');
  requisition_edit = permissionCheck('requisition-update');
  requisition_view = permissionCheck('requisition-view');
  requisition_logs = permissionCheck('requisition-activity-log');
  requisition_delete = permissionCheck('requisition-delete');

  // Fetch Requisition Types for filter dropdown
  const { data: requisitionsTypeData } = useQuery('all_requisition_types_for_filter', get_all_requisition_type, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.data && Array.isArray(data.data)) {
        setRequisitionTypeList(data.data as RequisitionType[]);
      }
    }
  });

  // --- Main Query for Filtered and Paginated Requisitions ---
  const {
    // data: filteredRequisitionsQueryData, // Full API response
    isLoading, // Use this for initial loading state
    isFetching, // Use this for subsequent loading states (filters, pagination)
    isError,
    error,
  } = useQuery<ApiRequisitionsFullResponse, Error>(
    ['filtered_requisitions', filterPayloadForApi, currentPage, itemsPerPage],
    () => get_filtered_requisitions(
      filterPayloadForApi.requisitionTypeId,
      filterPayloadForApi.fromDate,
      filterPayloadForApi.toDate,
      currentPage,
      itemsPerPage
      // filterPayloadForApi.searchTerm, // If server-side search
      // filterPayloadForApi.statusFilter, // If server-side status filter
    ),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const apiPayload = response.data.data;
        if (apiPayload && apiPayload.requisitions && Array.isArray(apiPayload.requisitions)) {
          setRequisitionList(apiPayload.requisitions);
          if (apiPayload.pagination) {
            setTotalItems(apiPayload.pagination.total);
            setTotalPages(apiPayload.pagination.last_page);
            setCurrentPage(apiPayload.pagination.current_page);
            setCurrentRangeFrom(apiPayload.pagination.from);
            setCurrentRangeTo(apiPayload.pagination.to);
          }
        } else {
          setRequisitionList([]);
          setTotalItems(0);
          setTotalPages(0);
          setCurrentRangeFrom(1);
          setCurrentRangeTo(0);
        }
      },
      onError: (error: any) => {
        toast.error(error.message || "Failed to fetch requisitions.", { autoClose: 3000 });
        setRequisitionList([]);
        setTotalItems(0);
        setTotalPages(0);
      },
    }
  );

  // Client-side search (optional)
  const displayedRequisitionList = searchInput
    ? requisitionList.filter((req) =>
        req.title?.toLowerCase().includes(searchInput.toLowerCase()) ||
        req.requisition_type_title?.toLowerCase().includes(searchInput.toLowerCase())
      )
    : requisitionList;

  // Mutation for updating status (Accept/Reject)
  const updateRequisitionStatus = useMutation(
    ({ id, statusFlag }: { id: number; statusFlag: string }) => get_requisitions_status(id, statusFlag),
    {
      onSuccess: (response, variables) => { // variables contains { id, statusFlag }
        toast.success(`Requisition status updated to ${variables.statusFlag}.`, { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_requisitions', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any, variables) => {
        const message = error.response?.data?.message || `Failed to update status to ${variables.statusFlag}.`;
        toast.error(message, { autoClose: 3000 });
      },
    }
  );

  const handleStatusAccepted = (id: number) => {
    updateRequisitionStatus.mutate({ id, statusFlag: 'Accepted' });
  };
  const handleStatusRejected = (id: number) => {
    updateRequisitionStatus.mutate({ id, statusFlag: 'Rejected' });
  };

  // Mutation for deleting requisition
  const handleDeleteRequisition = useMutation(
    (id: number) => delete_requisition(id),
    {
      onSuccess: (response) => {
        toast.success(response.message || "Requisition deleted successfully.", { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_requisitions', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || error.message || "Failed to delete requisition.";
        toast.error(errorMessage, { autoClose: 3000 });
      },
    }
  );
  
  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to page 1
    setFilterPayloadForApi({
      requisitionTypeId: requisitionTypeId,
      fromDate: fromDate,
      toDate: toDate,
      // searchTerm: searchInput, // If server-side search
      // statusFilter: statusFilterState, // If server has status filter in main query
    });
  };

  const handleClearFilters = () => {
    if (fromDateRef.current) fromDateRef.current.flatpickr.clear();
    if (toDateRef.current) toDateRef.current.flatpickr.clear();
    setFromDate('');
    setToDate('');
    setRequisitionTypeId('');
    setSearchInput('');

    setCurrentPage(1);
    setFilterPayloadForApi({
      requisitionTypeId: '', fromDate: '', toDate: '', /* searchTerm: '', statusFilter: '' */
    });
  };

  // Date picker handlers
  const handleDateFromChange = (selectedDates: Date[]) => {
    if (selectedDates && selectedDates[0]) {
      const formatted = formatDateFlatpickr(selectedDates[0]);
      setFromDate(formatted);
      if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', selectedDates[0]);
    } else {
      setFromDate('');
      if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', null);
    }
  };
  const handleDateToChange = (selectedDates: Date[]) => {
    if (selectedDates && selectedDates[0]) {
      const formatted = formatDateFlatpickr(selectedDates[0]);
      setToDate(formatted);
    } else {
      setToDate('');
    }
  };

  // Pagination handlers
  const itemsPerPageOptions = [10, 20, 30, 50, 100]; // Moved from itemsPerPage
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
   const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };


  // Navigation handlers
  const handleRequisitionLogs = (req: Requisition) => navigate("/requisitions/requisition-logs", { state: { id: req.id } });
  const handleEditRequisition = (req: Requisition) => navigate("/requisitions/edit-requisition", { state: { requisition: req } });
  const handleViewRequisition = (req: Requisition) => navigate("/requisitions/view-requisition", { state: { id: req.id } });
  const handleManageDocuments = (req: Requisition) => navigate("/requisitions/requisition-document", { state: { id: req.id } });

  // Removed client-side sorting. Server-side sorting would require backend changes
  // and updating filterPayloadForApi with sort parameters.

  if (isLoading && !isFetching && currentPage === 1) { // Initial load
    return <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}><div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Loading...</span></div></div>;
  }


  return (
    <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
      {/* Card Header: Title and Add Button */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Requisitions List</span>
           <span className='text-muted mt-1 fw-semibold fs-7'>
            {totalItems > 0 ? `${totalItems} requisitions found` : 'No requisitions'}
          </span>
        </h3>
        <div className='card-toolbar'>
          {requisition_add && (
            <button className='btn btn-sm btn-light-dark' onClick={() => navigate('/requisitions/add-requisition')}>
              <KTIcon iconName='plus' className='fs-3' /> Add Requisition
            </button>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className='card-body pt-0'>
        <div className='row mb-4 align-items-center gy-3'> {/* gy-3 for vertical gap on small screens */}
          <div className='col-md-3'>
            <div className='input-group'>
              <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='Search title, type...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className='col-md-3'>
            <select
              className='form-select form-select-solid'
              value={requisitionTypeId}
              onChange={(e) => setRequisitionTypeId(e.target.value)}
            >
              <option value=''>Requisition Type...</option>
              {requisitionTypeList.map((reqType) => (
                <option key={reqType.id} value={reqType.id}>{reqType.title}</option>
              ))}
            </select>
          </div>
          <div className='col-md-2'>
            <Flatpickr
              ref={fromDateRef}
              onChange={handleDateFromChange}
              className='form-control form-control-solid'
              placeholder='From Date (Due)'
              options={{ dateFormat: 'd/m/Y' }}
            />
          </div>
          <div className='col-md-2'>
            <Flatpickr
              ref={toDateRef}
              onChange={handleDateToChange}
              className='form-control form-control-solid'
              placeholder='To Date (Due)'
              options={{ dateFormat: 'd/m/Y' }}
            />
          </div>
          <div className='col-md-2 d-flex justify-content-end align-items-center'>
            <button className='btn btn-light-danger me-2' onClick={handleClearFilters} disabled={isFetching} style={{whiteSpace: 'nowrap'}}>
              <KTIcon iconName='cross' className='fs-5 me-1' /> Clear
            </button>
            <button className='btn btn-primary' onClick={handleApplyFilters} disabled={isFetching} style={{whiteSpace: 'nowrap'}}>
              <KTIcon iconName='filter-search' className='fs-5 me-1' /> Apply
            </button>
          </div>
        </div>
      </div>


      {/* Table Section */}
      <div className='card-body py-3'>
        {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating requisition list...</i></div>}
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='ps-4 min-w-200px rounded-start'>Title</th>
                <th className='min-w-150px'>Requisition Type</th>
                <th className='min-w-120px'>Due Date</th>
                <th className='min-w-120px'>Status</th>
                <th className='min-w-150px'>Updated By</th>
                <th className='min-w-150px'>Updated At</th>
                <th className='min-w-120px text-center'>Docs</th>
                {(requisition_delete || requisition_edit || requisition_logs || requisition_view) &&
                  <th className='min-w-150px text-center'>Actions</th>
                }
                {(requisition_edit || requisition_add) &&
                  <th className='min-w-150px text-center rounded-end'>Approve/Reject</th>
                }
              </tr>
            </thead>
            <tbody>
              {isLoading && currentPage === 1 ? (
                <tr><td colSpan={9} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
              ) : isError ? (
                <tr><td colSpan={9} className="text-center py-5 text-danger">Error: {error?.message}</td></tr>
              ) : displayedRequisitionList.length > 0 ? (
                displayedRequisitionList.map((req) => (
                  <tr key={req.id}>
                    <td className='ps-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6 cursor-pointer' onClick={() => handleViewRequisition(req)}>
                        {req.title}
                      </span>
                      <span className='text-muted fw-semibold d-block fs-7'>
                        {req.description?.substring(0, 40) || ''}...
                      </span>
                    </td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{req.requisition_type_title}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithoutTime(req.due_date)}</span></td>
                    <td>
                      <span className={`badge badge-lg ${
                        req.status === 'Accepted' ? 'badge-light-success' :
                        req.status === 'Rejected' ? 'badge-light-danger' :
                        req.status === 'Pending' ? 'badge-light-warning' : 'badge-light-primary' // Default for other statuses
                      } fs-7 fw-bold`}>
                        {req.status}
                      </span>
                    </td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{req.updated_by_name}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(req.updated_at)}</span></td>
                    <td className='text-center'>
                      <button onClick={() => handleManageDocuments(req)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm' title="Manage Documents">
                        <KTIcon iconName='file-up' className='fs-3' />
                      </button>
                    </td>
                    {(requisition_delete || requisition_edit || requisition_logs || requisition_view) &&
                    <td className='text-center'>
                      <div className='d-flex justify-content-center flex-shrink-0'>
                        {requisition_view && (<button onClick={() => handleViewRequisition(req)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View"><KTIcon iconName='eye' className='fs-3' /></button>)}
                        {requisition_edit && (<button onClick={() => handleEditRequisition(req)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                        {requisition_logs && (<button onClick={() => handleRequisitionLogs(req)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)}
                        {requisition_delete && (<button onClick={() => setSelectedRequisition(req)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_requisition'><KTIcon iconName='trash' className='fs-3' /></button>)}
                      </div>
                    </td>
                    }
                     {(requisition_edit || requisition_add) &&
                    <td className='text-center'>
                      <div className='d-flex justify-content-center flex-shrink-0'>
                        <button
                          onClick={() => {
                            if (req.has_quotation) toast.error('This requisition has quotations and cannot be updated.', {autoClose: 3000});
                            else handleStatusAccepted(req.id);
                          }}
                          className={`btn btn-icon btn-sm me-1 ${req.status === 'Accepted' ? 'btn-success' : 'btn-light-success btn-active-color-success'}`}
                          title="Approve"
                          disabled={req.status === 'Accepted' || req.status === 'Rejected' || updateRequisitionStatus.isLoading}
                        > <KTIcon iconName="check-circle" className="fs-3" /> </button>
                        <button
                          onClick={() => {
                             if (req.has_quotation) toast.error('This requisition has quotations and cannot be updated.', {autoClose: 3000});
                             else handleStatusRejected(req.id);
                          }}
                          className={`btn btn-icon btn-sm ${req.status === 'Rejected' ? 'btn-danger' : 'btn-light-danger btn-active-color-danger'}`}
                          title="Reject"
                          disabled={req.status === 'Accepted' || req.status === 'Rejected' || updateRequisitionStatus.isLoading}
                        > <KTIcon iconName="cross-circle" className="fs-3" /> </button>
                      </div>
                    </td>
                    }
                  </tr>
                ))
              ) : (
                 !isFetching && <tr><td colSpan={9} className="text-center py-5">No requisitions found matching your criteria.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: Pagination and Items Per Page */}
       {totalItems > 0 && (
      <div className='card-footer d-flex justify-content-between align-items-center py-4'>
          <div>
            <span className='text-muted me-2'>Showing:</span>
            <select
              className='form-select form-select-sm d-inline-block w-auto me-3'
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              disabled={isFetching}
            >
              {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
            </select>
            <span className='text-muted'>
              {currentRangeFrom} - {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} requisitions
            </span>
          </div>
        
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 || isFetching ? 'disabled' : ''}`}>
                <button className="page-link" onClick={handlePreviousPage} disabled={currentPage === 1 || isFetching}>Previous</button>
              </li>
              {/* Optional: Add Page Number Logic */}
              <li className={`page-item ${currentPage === totalPages || totalPages === 0 || isFetching ? 'disabled' : ''}`}>
                <button className="page-link" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0 || isFetching}>Next</button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <div className="modal fade" tabIndex={-1} id="kt_modal_delete_requisition">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete Requisition Confirmation</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the requisition: <br/>
                <strong>{selectedRequisition?.title}</strong>?
              </p>
              <p className='text-muted small'>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => { if (selectedRequisition) handleDeleteRequisition.mutate(selectedRequisition.id); }}
                data-bs-dismiss="modal"
                disabled={handleDeleteRequisition.isLoading}
              >
                {handleDeleteRequisition.isLoading ? 'Deleting...' : 'Delete Requisition'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RequisitionsList;